import React, { useEffect, useMemo, useCallback } from 'react';
import { connect, useDispatch } from 'react-redux';
import { IEventsState, IScholarsState, IUser } from 'types';
import { IRootState } from 'store';
import { getTimeWithTimeZone } from 'utils/helpers/momentTime';
import moment from 'moment';
import { getEvents } from 'actions/events';
import _ from 'lodash';
import { calculateTime, getActiveOffEvent } from 'utils/helpers/offlineSpinner';
import OfflineSpinner from 'components/offline-activity-animation/offlineSpinner';
import { hideOfflineSpinner, showOfflineSpinner } from 'actions/app-state';
import { getScholar } from 'actions/scholars';
import { hideSpinner, showSpinner } from 'actions/spinner';
import MapComponent from 'components/common/maps/mapComponent';
import { defaultMapTemplate } from 'utils/constants/map';
import './index.scss';

const mapStateToProps = (state: IRootState) => ({
  scholars: state.scholars,
  events: state.events,
  userData: state.users.userData,
  showStatusOfflineSpinner: state.appState.showStatusOfflineSpinner,
  offlineSpinnerUnitId: state.appState.offlineSpinnerUnitId,
});

interface MapProps {
  events: IEventsState;
  userData: IUser | null | undefined;
  scholars: IScholarsState;
  showStatusOfflineSpinner: boolean;
  offlineSpinnerUnitId: number | undefined;
}

const Map: React.FC<MapProps> = ({
  events,
  userData,
  scholars,
  showStatusOfflineSpinner,
  offlineSpinnerUnitId,
}) => {
  const { scholar } = scholars;

  const activeScholarId = userData?.activeScholarId;

  const noScholars =
    scholar === null &&
    userData &&
    !activeScholarId &&
    scholars.units?.length === 0;

  const expireTimeField = localStorage.getItem('animationExpireTime');

  const dispatch = useDispatch();

  useEffect(() => {
    if (activeScholarId) {
      dispatch(getScholar(activeScholarId));
    }
  }, [activeScholarId, dispatch]);

  useEffect(() => {
    if (activeScholarId) {
      dispatch(
        getEvents(
          activeScholarId,
          moment().subtract(1, 'day').format(),
          moment().add(1, 'day').format()
        )
      );
    }
  }, [activeScholarId, dispatch]);

  const eventsWithTimezone = useMemo(() => {
    return _.map(events.eventsList, (el) => {
      return {
        ...el,
        startTimeStr: getTimeWithTimeZone(el.startTimeStr || '00:00', 'HH:mm'),
        endTimeStr: getTimeWithTimeZone(el.endTimeStr || '00:00', 'HH:mm'),
      };
    });
  }, [events.eventsList]);

  const sortedLessonsByTime = useMemo(() => {
    return _.sortBy(eventsWithTimezone, ['startTimeStr']).filter(
      (e) => moment(e.startTime).isSame(new Date(), 'day') && e
    );
  }, [eventsWithTimezone]);

  const activeOfflineEvent = useMemo(
    () => getActiveOffEvent(sortedLessonsByTime),
    [sortedLessonsByTime]
  );

  const timer = useMemo(() => calculateTime(activeOfflineEvent), [
    activeOfflineEvent,
  ]);

  const finishTime = useMemo(() => new Date().getTime() + timer.duration, [
    timer.duration,
  ]);

  const clearOfflineAnimationSpinner = useCallback(() => {
    localStorage.removeItem('animationExpireTime');
    localStorage.removeItem('offlineActivityUnitId');

    dispatch(hideOfflineSpinner());
  }, [dispatch]);

  // remove wrong or expired field value
  useEffect(() => {
    const expireTimeFieldIsNaN = Number.isNaN(
      parseFloat(expireTimeField || '')
    );

    if (expireTimeFieldIsNaN) {
      localStorage.removeItem('animationExpireTime');
    }
  }, [expireTimeField]);

  useEffect(() => {
    if (
      !!expireTimeField &&
      +expireTimeField < finishTime &&
      !activeOfflineEvent &&
      offlineSpinnerUnitId
    ) {
      clearOfflineAnimationSpinner();
    }
  }, [
    activeOfflineEvent,
    clearOfflineAnimationSpinner,
    expireTimeField,
    finishTime,
    offlineSpinnerUnitId,
  ]);

  useEffect(() => {
    if (
      !!expireTimeField &&
      +expireTimeField === finishTime &&
      !showStatusOfflineSpinner &&
      activeOfflineEvent
    ) {
      dispatch(showOfflineSpinner());
    }
  }, [
    activeOfflineEvent,
    dispatch,
    expireTimeField,
    finishTime,
    showStatusOfflineSpinner,
  ]);

  useEffect(() => {
    let timeOut: NodeJS.Timeout;
    if (showStatusOfflineSpinner && timer.duration) {
      timeOut = setTimeout(() => {
        clearOfflineAnimationSpinner();
      }, timer.duration);
    }
    return () => {
      clearTimeout(timeOut);
    };
  }, [clearOfflineAnimationSpinner, showStatusOfflineSpinner, timer.duration]);

  const mapTemplate = scholar?.settings?.mapTemplate || defaultMapTemplate;

  useEffect(() => {
    if (!scholar && !noScholars) {
      dispatch(showSpinner());
    } else {
      dispatch(hideSpinner());
    }

    if (noScholars) {
      dispatch(hideSpinner());
    }
  }, [dispatch, noScholars, scholar]);

  const lessons = _.take(sortedLessonsByTime, 12);

  return (
    <div className={`map-page ${(scholar || noScholars) && mapTemplate}`}>
      {(scholar || noScholars) && <MapComponent lessons={lessons} />}
      {showStatusOfflineSpinner && activeOfflineEvent && (
        <OfflineSpinner timer={timer} />
      )}
    </div>
  );
};

export default connect(mapStateToProps)(Map);
